import type { Breadcrumb, GenerateBreadcrumbsParams } from '@/entities/breadCrumbs';
import { withMfsDomain } from '@/shared/lib/withMfsDomain';
import type { CityInfo } from '@/shared/api/experience';

/**
 * Генерирует хлебные крошки для страниц экскурсий на основе переданных параметров
 * @param params Параметры для генерации хлебных крошек
 * @returns Массив хлебных крошек
 */
export function generateExperienceBreadcrumbs(params: GenerateBreadcrumbsParams): Breadcrumb[] {
  const { currentTag, geoObject, rubric, rubricTitlesMap, isExperienceDetailsPage = false } = params;

  const cityInfo = geoObject as CityInfo;
  const cityName = cityInfo?.name_ru || '';
  const citySlug = cityInfo?.slug || '';
  const countryName = cityInfo?.country?.name_ru || '';
  const countrySlug = cityInfo?.country?.slug || '';

  const resolvedTag = rubric ?? currentTag;
  const isRubricValid = !!rubricTitlesMap[resolvedTag];
  const finalTag = isRubricValid ? resolvedTag : null;

  const crumbs: Breadcrumb[] = [{ label: 'Главная', path: `/`, external: true }];

  if (!isExperienceDetailsPage) {
    // Для листинга экскурсий
    addListingBreadcrumbs(crumbs, {
      countryName,
      countrySlug,
      cityName,
      citySlug,
      finalTag,
      currentTag,
    });
  } else {
    // Для страницы экскурсии
    addExperiencePageBreadcrumbs(crumbs, {
      cityName,
      citySlug,
      finalTag,
      rubricTitlesMap,
    });
  }

  return crumbs;
}

/**
 * Добавляет хлебные крошки для страницы листинга экскурсий
 * @param crumbs Массив хлебных крошек для модификации
 * @param params Параметры для генерации хлебных крошек
 */
function addListingBreadcrumbs(
  crumbs: Breadcrumb[],
  params: {
    countryName: string;
    countrySlug: string;
    cityName: string;
    citySlug: string;
    finalTag: string | null;
    currentTag: string;
  },
): void {
  const { countryName, countrySlug, cityName, citySlug, finalTag, currentTag } = params;

  // Добавляем страну для всех случаев листинга
  crumbs.push({ label: countryName, path: `/destinations/${countrySlug}/`, external: true });

  // Для листинга города с фильтром добавляем также город
  if (finalTag && currentTag !== 'all') {
    crumbs.push({ label: cityName, path: withMfsDomain(`/experience/${citySlug}/`) });
  }
}

/**
 * Добавляет хлебные крошки для страницы экскурсии
 * @param crumbs Массив хлебных крошек для модификации
 * @param params Параметры для генерации хлебных крошек
 */
function addExperiencePageBreadcrumbs(
  crumbs: Breadcrumb[],
  params: {
    cityName: string;
    citySlug: string;
    finalTag: string | null;
    rubricTitlesMap: Record<string, string>;
  },
): void {
  const { cityName, citySlug, finalTag, rubricTitlesMap } = params;

  // Добавляем город
  crumbs.push({ label: cityName, path: withMfsDomain(`/experience/${citySlug}/`) });

  // Добавляем рубрику, если она есть
  if (finalTag) {
    crumbs.push({
      label: rubricTitlesMap[finalTag],
      path: withMfsDomain(`/experience/${citySlug}/${finalTag}/`),
    });
  }
}
